const RELOAD_AT_KEY = 'polygon:stale-chunk-reload-at';
const MIN_RELOAD_INTERVAL_MS = 30_000;

// `resolvePageComponent` rejects with this message when the page name the
// server returned is absent from the bundle's page glob.
const PAGE_NOT_FOUND = /^Page not found:/;

/**
 * Recover from a bundle that predates the running deploy. Two failure modes,
 * both fixed by picking up the fresh manifest:
 *
 * - a dynamic import of a hashed chunk 404s (Vite dispatches `vite:preloadError`)
 * - the server routes to a page name the old bundle's glob does not contain,
 *   which happens whenever a release adds a page — a module migrating to the
 *   generic admin pages is exactly that case
 *
 * The loop guard lets a repeated failure surface (and reach Sentry) instead of
 * reloading forever: a page that genuinely does not exist throws again after
 * the reload and is left alone.
 */
let installed = false;

export function installStaleChunkReload(): void {
    if (installed || typeof window === 'undefined') {
        return;
    }

    installed = true;

    window.addEventListener('vite:preloadError', (event) => {
        if (!reloadOnce()) {
            return;
        }

        event.preventDefault();
    });

    window.addEventListener('unhandledrejection', (event) => {
        if (!isMissingPage(event.reason)) {
            return;
        }

        if (!reloadOnce()) {
            return;
        }

        event.preventDefault();
    });
}

/**
 * Whether a rejection reason is Inertia failing to resolve a page name.
 */
function isMissingPage(reason: unknown): boolean {
    const message = typeof reason === 'string' ? reason : (reason as Error | null)?.message;

    return typeof message === 'string' && PAGE_NOT_FOUND.test(message);
}

/**
 * Reload unless one was already triggered inside the guard interval.
 */
function reloadOnce(): boolean {
    const lastReloadAt = Number(sessionStorage.getItem(RELOAD_AT_KEY) ?? 0);

    if (Date.now() - lastReloadAt < MIN_RELOAD_INTERVAL_MS) {
        return false;
    }

    sessionStorage.setItem(RELOAD_AT_KEY, String(Date.now()));
    window.location.reload();

    return true;
}
